import { SAMPLE_REQUEST_TYPES } from '@/constants/constant';

import { VALIDATION_SCHEMAS } from '@/features/sampleDevelopment/constants';
import { _getKey, checkAnyUserType, replaceUnderLine, checkUserType } from '@/utils/Util';

export const validateFormFields = (requestType, requestValues, setInputErrors) => {
	const schema = VALIDATION_SCHEMAS[requestType];
	if (!schema) return true;

	const newErrors = {};

	// Helper functions for role checking
	const isProjectManager = () => checkUserType('PROJECT_MANAGER');
	const isAccountManager = () => checkUserType('ACCOUNT_MANAGER');
	const isBusinessDevelopmentManager = () => checkUserType('BUSINESS_DEVELOPMENT_MANAGER');
	const isAdmin = () => checkUserType('ADMIN');

	// Special handling for TP_HANDOVER
	if (
		requestType === SAMPLE_REQUEST_TYPES.TP_HANDOVER ||
		requestType === SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_PROJECT_TEAM
	) {
		// Always validate handoverPurpose
		if (!requestValues.handoverPurpose) {
			newErrors.handoverPurpose = 'Handover Purpose is required';
		}

		// Validate brand (Customer Name)
		if (!requestValues.brand) {
			newErrors.brand = 'Customer Name is required';
		}

		// Validate projectDealName (Deal)
		if (!requestValues.projectDealName) {
			newErrors.projectDealName = 'Deal is required';
		}

		// Conditionally validate brand and handoverToPerson
		// Only require them when NOT (ASSIGN_PATTERN_MASTER or HANDOVER_TO_PATTERN_SECTION)
		// const isOptionalFields =
		// 	requestValues?.handoverPurpose?.constant === 'ASSIGN_PATTERN_MASTER' ||
		// 	requestValues?.handoverPurpose?.constant === 'HANDOVER_TO_PATTERN_SECTION';

		const handoverToPersonArray = Array.isArray(requestValues.handoverToPerson)
			? requestValues.handoverToPerson
			: [];

		if (handoverToPersonArray.length === 0) {
			newErrors.handoverToPerson = 'Handover to person is required';
		}
	} else {
		const canAccessAmSection =
			(isAccountManager() || isBusinessDevelopmentManager() || isAdmin()) && !isProjectManager();

		// Original validation logic for other request types
		Object.entries(schema).forEach(([fieldName, rules]) => {
			// Skip validation for disabled fields based on role
			if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_FACTORY_BUYER_DESIGN) {
				// Skip factorySubmitionToNitexDate validation if user is NOT PROJECT_MANAGER
				if (fieldName === 'factorySubmitionToNitexDate' && !isProjectManager()) {
					return;
				}
				// Skip AM section date validation when AM section is not accessible for this role mix.
				if (
					(fieldName === 'buyerRequiredDate' || fieldName === 'expectedOrderDate') &&
					!canAccessAmSection
				) {
					return;
				}
			}

			const value =
				fieldName === 'buyerRequiredDate' ? requestValues?.requiredDate : requestValues[fieldName];
			if (
				rules.required &&
				(!value || value?.length === 0 || (typeof value === 'string' && !value.trim()))
			) {
				newErrors[fieldName] = rules.message;
			}
		});

		if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_PD_IN_HOUSE) {
			const sampleTypeValue = requestValues?.sampleType?.value?.toLowerCase();
			const customerName = requestValues?.primaryTargetCustomar?.name?.trim().toLowerCase();
			const isNitexCustomer = customerName === 'nitex';
			const isDealRequired =
				!isNitexCustomer &&
				(sampleTypeValue === 'nitex development' || sampleTypeValue === 'buyer development');

			if (isDealRequired && !requestValues?.projectDealName) {
				newErrors.projectDealName = 'Deal is required';
			}
		}
	}

	setInputErrors(newErrors);
	return Object.keys(newErrors).length === 0;
};

export const validateSampleDetails = (selectedDesigns, setSampleDetailsErrors) => {
	const errorDesignIds = new Set();
	let hasAtLeastOneValidRow = false;

	selectedDesigns.forEach((design) => {
		let designHasValidRow = false;

		if (design?.isSet) {
			design?.setItemsList?.forEach((member) => {
				let memberHasValidRow = false;
				member?.colorResponseList?.forEach((color) => {
					// Only quantity is mandatory, size and color are optional
					if (color?.quantity) {
						// Validate quantity is between 1 and 4
						if (parseInt(color.quantity) < 1 || parseInt(color.quantity) > 4) {
							errorDesignIds.add(member.id);
							errorDesignIds.add(design.id);
						} else {
							memberHasValidRow = true;
							designHasValidRow = true;
						}
					}
				});

				if (!memberHasValidRow) {
					errorDesignIds.add(member.id);
					errorDesignIds.add(design.id);
				}
			});
		} else {
			design?.colorResponseList?.forEach((color) => {
				// Only quantity is mandatory, size and color are optional
				if (color?.quantity) {
					// Validate quantity is between 1 and 4
					if (parseInt(color.quantity) < 1 || parseInt(color.quantity) > 4) {
						errorDesignIds.add(design.id);
					} else {
						designHasValidRow = true;
					}
				}
			});

			if (!designHasValidRow) {
				errorDesignIds.add(design.id);
			}
		}

		if (designHasValidRow) {
			hasAtLeastOneValidRow = true;
		}
	});

	if (!hasAtLeastOneValidRow) {
		selectedDesigns.forEach((design) => errorDesignIds.add(design.id));
	}

	setSampleDetailsErrors([...errorDesignIds]);
	return errorDesignIds.size === 0;
};

export const checkSizeError = (item, colorKey, colorPopupErrors = []) => {
	return colorPopupErrors.some(
		(err) => err?.errorDesignId === item.id && err?.errorKey === colorKey && err?.type === 'SIZE'
	);
};

export const checkQuantityError = (item, colorKey, colorPopupErrors = []) => {
	return colorPopupErrors.some(
		(err) => err?.errorDesignId === item.id && err?.errorKey === colorKey && err?.type === 'QUANTITY'
	);
};

export const updateColors = (color, name, value) => {
	if (name === 'code' && !value) {
		color[name] = value;
		color['hexCode'] = null;
		color['pantoneColorId'] = null;
	} else {
		color[name] = value;
	}
};

export const createColorDTO = (color) => {
	if (color?.code || color?.name) {
		return {
			name: color.code || color.name,
			colorType: color.colorType || 'SOLID',
			representedBy: 'PANTONE_OR_HEX_CODE',
			compositeColorList: [],
			pantoneColorId: color.pantoneColorId,
			swatchDocumentId: null
		};
	}
	return null;
};

export const formatColorResponseList = (colorResponseList = []) => {
	const newColorResponse = [];
	colorResponseList?.forEach((color) => {
		// Only quantity is mandatory, size and color are optional
		if (color?.quantity) {
			newColorResponse.push({
				id: color?.sampleItemDetailsId || null,
				colorDTO: createColorDTO(color?.colorResponse || color),
				quantity: color.quantity || null,
				size: color.size || null,
				recipientIdList: color?.sampleItemDetailsRecipientMapResponse?.map(
					(user) => user?.userResponse?.id || user?.id
				)
			});
		}
	});
	return newColorResponse;
};

export const formatOptions = (data, key) => {
	return data?.map((item) => ({
		...item,
		value: item[key]
	}));
};

export const formatSampleTypeOptions = (sampleTypeList) => {
	return sampleTypeList?.map((sampleType) => ({
		...sampleType,
		id: sampleType?.constant,
		value: replaceUnderLine(sampleType?.name)
	}));
};

export const formatNitexOperationOptions = (nitexOperationList) => {
	return nitexOperationList?.map((operation) => ({
		...operation,
		value: operation.name,
		label: operation.name
	}));
};

export const formatProjectOptions = (projectList) => {
	return projectList?.map((project) => ({
		...project,
		value: project.text
	}));
};

export const formatProjectDealOptions = (projectDealList) => {
	return projectDealList?.map((dealList) => ({
		...dealList,
		value: dealList?.dealName
	}));
};

export const formatBrandOptions = (brandList) => {
	return brandList?.map((brand) => ({
		...brand,
		value: brand.name
	}));
};

export const formatFactoryOptions = (factoryList) => {
	return factoryList?.data?.map((factory) => ({
		...factory,
		value: factory.name
	}));
};

export const isCheckListCompleted = (products = [], setErrors) => {
	if (!Array.isArray(products)) return false;

	const errorProductIds = [];

	for (const item of products) {
		if (item?.isSet) {
			if (
				Array.isArray(item.setMemberProducts) &&
				item.setMemberProducts.some((member) => member?.checklistStatus !== 'COMPLETED')
			) {
				errorProductIds.push(item?.id);
			}
		} else if (item?.checklistStatus !== 'COMPLETED') {
			errorProductIds.push(item?.id);
		}
	}

	if (errorProductIds.length > 0) {
		setErrors((prev) => [...prev, ...errorProductIds]);
		return false;
	}

	return true;
};

export const hasRequestedBefore = (products = []) => {
	if (!Array.isArray(products)) return false;

	return products.some((item) => {
		if (item?.isSet) {
			return (
				Array.isArray(item.setMemberProducts) &&
				item.setMemberProducts.length > 0 &&
				item.setMemberProducts.some((member) => member?.pendingSampleCount > 0)
			);
		} else {
			return item?.pendingSampleCount > 0;
		}
	});
};

export const mapSampleDataToColorResponse = (sampleRequestDataArray) => {
	const sampleRequestMap = new Map();

	sampleRequestDataArray?.forEach((sampleRequestData) => {
		const productId = sampleRequestData?.productId;

		sampleRequestData?.sampleItemDetailsResponseList?.forEach((item) => {
			const colorResponse = {
				key: _getKey(),
				code: item?.colorResponse?.code,
				hexCode: item?.colorResponse?.hexCode || null,
				pantoneColorId: item?.colorResponse?.pantoneColorId || null,
				colorType: item?.colorResponse?.colorType || 'SOLID',
				representedBy: item?.colorResponse?.representedBy || 'PANTONE_OR_HEX_CODE',
				size: item?.size || null,
				quantity: item?.quantity || null
			};

			const existingData = sampleRequestMap.get(productId);
			if (existingData) {
				existingData.colorResponseList.push(colorResponse);
			} else {
				sampleRequestMap.set(productId, {
					colorResponseList: [colorResponse],
					remarks: sampleRequestData.remarks || ''
				});
			}
		});
	});

	return sampleRequestMap;
};

const createColorRows = (length = 0) => {
	const result = [];
	for (let i = 0; i < length; i++) {
		result.push({});
	}
	return result;
};

export const applySampleDataToDesigns = (designs, sampleRequestMap, isFromTechpack) => {
	return designs.map((design) => {
		const sampleData = sampleRequestMap.get(design.id);
		if (sampleData) {
			if (design.isSet) {
				return {
					...design,
					setItemsList:
						design.setItemsList?.map((setMember) => {
							const setMemberData = sampleRequestMap.get(setMember.id);
							return setMemberData ? { ...setMember, ...setMemberData } : setMember;
						}) || [],
					remarks: sampleData.remarks
				};
			} else {
				return {
					...design,
					colorResponseList: isFromTechpack
						? design.colorResponseList
						: [
								...sampleData?.colorResponseList,
								...createColorRows(
									Math.max(0, 1 - (sampleData?.colorResponseList?.length || 0))
								)
							] || design.colorResponseList,
					remarks: sampleData.remarks || design.remarks
				};
			}
		}

		return design;
	});
};

const CHAR_SET = 'abcdefghijklmnopqrstuvwxyz123456789';

export const generateStaybleId = (length = 8) => {
	let id = '';
	for (let i = 0; i < length; i++) {
		const randomIndex = Math.floor(Math.random() * CHAR_SET.length);
		id += CHAR_SET[randomIndex];
	}

	return id;
};

export const isTPHandover = (taskValueType) => {
	return taskValueType === SAMPLE_REQUEST_TYPES.TP_HANDOVER;
};

export const isSampleDevPurpose = (purpose) => {
	return purpose === 'SAMPLE_DEVELOPMENT';
};

export const isShowProductDetailsOnTable = (taskValueType, purpose) => {
	return (isTPHandover(taskValueType) && isSampleDevPurpose(purpose)) || !isTPHandover(taskValueType);
};

export const isSampleTypeRequired = (constant) => {
	return constant === 'SAMPLE_DEVELOPMENT' || constant === 'COMPLETE_TP';
};

// Buyer development sample cannot be requested for the Nitex brand
export const isBuyerDevelopmentForNitex = (requestType, requestValues) => {
	const applicableTypes = [
		SAMPLE_REQUEST_TYPES.SEND_TO_PD_IN_HOUSE,
		SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_PROJECT_TEAM,
		SAMPLE_REQUEST_TYPES.SEND_TO_FACTORY_BUYER_DESIGN
	];
	if (!applicableTypes.includes(requestType)) return false;

	const customer =
		requestType === SAMPLE_REQUEST_TYPES.SEND_TO_PD_IN_HOUSE
			? requestValues?.primaryTargetCustomar
			: requestValues?.brand;
	const customerName = customer?.name?.trim().toLowerCase();

	const sampleType = requestValues?.sampleType;
	const isBuyerDevelopment =
		sampleType?.constant === 'BUYER_DEVELOPMENT' ||
		sampleType?.id === 'BUYER_DEVELOPMENT' ||
		sampleType?.value?.trim().toLowerCase() === 'buyer development';

	return customerName === 'nitex' && isBuyerDevelopment;
};

export const validateSampleTypeInTPHandover = (inputValues, setErrors) => {
	if (isSampleTypeRequired(inputValues?.handoverPurpose?.constant) && !inputValues?.sampleType) {
		setErrors((prev) => ({
			...prev,
			sampleType: 'Sample type is required'
		}));
		return false;
	}
	return true;
};

export const isValidTpHandoverDate = (inputValues, setErrors) => {
	const newErrors = {};
	if (
		isSampleDevPurpose(inputValues?.handoverPurpose?.constant) &&
		!inputValues?.requiredDate &&
		checkAnyUserType(['ACCOUNT_MANAGER', 'BUSINESS_DEVELOPMENT_MANAGER'])
	) {
		setErrors((prev) => ({
			...prev,
			requiredDate: 'Customer Required date is required'
		}));
		newErrors.requiredDate = 'Customer Required date is required';
	}
	if (
		isSampleDevPurpose(inputValues?.handoverPurpose?.constant) &&
		!inputValues?.expectedOrderDate &&
		checkAnyUserType(['ACCOUNT_MANAGER', 'BUSINESS_DEVELOPMENT_MANAGER'])
	) {
		setErrors((prev) => ({
			...prev,
			expectedOrderDate: 'Expected order date is required'
		}));
		newErrors.expectedOrderDate = 'Expected order date is required';
	}
	return Object.keys(newErrors).length === 0;
};

export const getInitialStateForSampleRequest = (requestType) => {
	const baseState = {
		primaryTargetCustomar: null,
		projectName: null,
		sampleType: null,
		requiredDate: '',
		buyerRequiredDate: '',
		sendSampleTo: [],
		expectedOrderDate: '',
		factoryName: null,
		factorySubmitionToNitexDate: '',
		requestFor: null,
		brand: null,
		handoverToPerson: [],
		handoverPurpose: null,
		note: ''
	};

	// Set default requestFor for SAMPLE request type
	if (requestType === 'SAMPLE') {
		return {
			...baseState,
			requestFor: {
				id: 'SEND_TO_SAMPLE_SECTION',
				index: 0,
				isAddNew: false,
				label: 'Send to sample section',
				title: 'Select request for',
				value: 'Send to sample section'
			}
		};
	}

	return baseState;
};

export const getUserTypeFromTaskValue = (taskValueType) => {
	switch (taskValueType) {
		case 'TP_HANDOVER_TO_PROJECT_TEAM':
			return 'PROJECT_MANAGER,MERCHANDISER';
		case 'TP_HANDOVER_TO_PATTERN_SECTION':
			return 'SAMPLE_SECTION_LEAD';
		case 'TP_HANDOVER_TO_ACCOUNT_MANAGER':
			return 'ACCOUNT_MANAGER';
		case 'TP_HANDOVER_TO_DESIGNER':
			return 'FASHION_DESIGNER';
		default:
			return '';
	}
};

export const formatUserTypes = (userList = {}, types = []) => {
	const getTypes = [];
	types?.forEach((type) => {
		if (userList[type]) {
			getTypes.push(...userList[type]);
		}
	});

	const users = getTypes?.map((user) => {
		return {
			...user,
			label: `${user?.country?.code ? user?.country?.code : ''}${user?.country?.code ? ' | ' : ''}${user.name}`,
			value: user.name
		};
	});
	return users;
};
